import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { Inbox, Phone, Mail, TrendingUp, Clock, CheckCircle2, XCircle, AlertCircle, Filter } from "lucide-react";

const STATUSES = [
  { id: "all",       label: "All",        color: "text-zinc-300" },
  { id: "new",       label: "New",        color: "text-[#FFB000]" },
  { id: "contacted", label: "Contacted",  color: "text-[#60A5FA]" },
  { id: "qualified", label: "Qualified",  color: "text-[#00E676]" },
  { id: "won",       label: "Won",        color: "text-[#00E676]" },
  { id: "lost",      label: "Lost",       color: "text-[#FF3B30]" },
];

const INTERESTS = { scale: "Scale", enterprise: "Enterprise", sovereign: "Sovereign", sso: "SSO", general: "General" };

export default function AdminCalls() {
  const [auth, setAuth] = useState({ checked: false, allowed: false });
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [hot, setHot] = useState([]);
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/me").then(r => {
      setAuth({ checked: true, allowed: r.data.is_super_admin });
    }).catch(() => setAuth({ checked: true, allowed: false }));
  }, []);

  useEffect(() => {
    if (!auth.allowed) return;
    refresh();
    api.get("/admin/upsell-leads").then(r => setHot(r.data || [])).catch(() => {});
  }, [auth.allowed, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => {
    api.get(`/admin/sales-leads`, { params: { status } })
      .then(r => setLeads(r.data || []))
      .catch(() => {});
    api.get("/admin/sales-leads/stats").then(r => setStats(r.data)).catch(() => {});
  };

  const updateStatus = async (id, newStatus) => {
    setSaving(true);
    try {
      await api.patch(`/admin/sales-leads/${id}`, { status: newStatus, note: noteDraft });
      toast.success(`Marked ${newStatus}.`);
      setSelected(null);
      setNoteDraft("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  const selectedLead = useMemo(() => leads.find(l => l.id === selected) || null, [selected, leads]);

  if (!auth.checked) return <div className="p-10 font-mono text-[11px] text-zinc-500">CHECKING CREDENTIALS…</div>;
  if (!auth.allowed) return (
    <div className="p-10" data-testid="admin-denied">
      <h1 className="font-heading font-black text-3xl uppercase tracking-tighter">403 — Super-admin only.</h1>
      <p className="text-sm text-zinc-500 mt-2 max-w-md">
        This triage console is reserved for DARA Systems staff. Set <code className="text-[#FFB000]">SUPER_ADMIN_EMAIL</code> in the backend environment to grant your email access.
      </p>
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="admin-calls">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · SALES INBOX</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter uppercase mt-1 flex items-center gap-3">
          <Inbox className="w-7 h-7 text-[#FFB000]" /> Leads
        </h1>
        <p className="text-sm text-zinc-500 mt-2">Book-a-Call submissions + hot upgrade prospects. Triage, note, and close.</p>
      </div>

      {/* status counts */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-zinc-900 border border-zinc-900" data-testid="status-counts">
        {STATUSES.map(s => {
          const v = s.id === "all" ? (stats?.total ?? 0) : (stats?.[s.id] ?? 0);
          return (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              data-testid={`filter-${s.id}`}
              className={`bg-black p-4 text-left hover:bg-[#080808] transition-colors ${status === s.id ? "ring-1 ring-inset ring-[#FFB000]" : ""}`}
            >
              <div className={`font-mono text-[9px] uppercase tracking-widest ${s.color}`}>{s.label}</div>
              <div className="font-mono text-2xl font-medium text-white mt-1">{v}</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* leads table */}
        <div className="lg:col-span-2 space-y-4">
          <Panel title={`// BOOK A CALL · ${status.toUpperCase()} (${leads.length})`} meta={<Filter className="w-3.5 h-3.5" />}>
            <div className="divide-y divide-zinc-900 max-h-[600px] overflow-auto">
              {leads.length === 0 && (
                <div className="p-8 text-center font-mono text-[11px] text-zinc-600">No submissions in this bucket.</div>
              )}
              {leads.map(l => (
                <button
                  key={l.id}
                  onClick={() => { setSelected(l.id); setNoteDraft(""); }}
                  data-testid={`lead-${l.id}`}
                  className={`w-full text-left p-4 hover:bg-[#080808] transition-colors ${selected === l.id ? "bg-[#FFB000]/5 border-l-2 border-[#FFB000]" : ""}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-heading font-bold text-sm">{l.name}</span>
                        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">· {l.company}</span>
                      </div>
                      <div className="flex gap-4 font-mono text-[10px] text-zinc-400">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</span>
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-[#FFB000]/30 text-[#FFB000]">
                          {INTERESTS[l.interest] || l.interest}
                        </span>
                        <span className="font-mono text-[9px] text-zinc-600">
                          {l.company_size} · {l.preferred_time || "—"}
                        </span>
                      </div>
                    </div>
                    <StatusBadge status={l.status} />
                  </div>
                  {l.message && <div className="mt-2 font-mono text-[10px] text-zinc-500 line-clamp-2">“{l.message}”</div>}
                  <div className="mt-1 font-mono text-[9px] text-zinc-700">{new Date(l.created_at).toLocaleString()}</div>
                </button>
              ))}
            </div>
          </Panel>
        </div>

        {/* right column */}
        <div className="space-y-4">
          {/* triage panel */}
          {selectedLead ? (
            <Panel title={`// TRIAGE · ${selectedLead.name.toUpperCase()}`}>
              <div className="p-4 space-y-4">
                <div className="space-y-2 font-mono text-[11px]">
                  <Row label="Company" value={selectedLead.company} />
                  <Row label="Email"   value={<a href={`mailto:${selectedLead.email}`} className="text-[#FFB000] hover:underline" data-testid="lead-email-link">{selectedLead.email}</a>} />
                  <Row label="Phone"   value={<a href={`tel:${selectedLead.phone}`} className="text-[#FFB000] hover:underline" data-testid="lead-phone-link">{selectedLead.phone}</a>} />
                  <Row label="Size"    value={selectedLead.company_size} />
                  <Row label="Plan"    value={INTERESTS[selectedLead.interest] || selectedLead.interest} />
                  <Row label="When"    value={selectedLead.preferred_time || "—"} />
                  <Row label="Status"  value={<StatusBadge status={selectedLead.status} />} />
                </div>
                {selectedLead.message && (
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Message</div>
                    <div className="font-mono text-[11px] text-zinc-300 bg-black border border-zinc-800 p-3">{selectedLead.message}</div>
                  </div>
                )}
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Add note (optional)</div>
                  <textarea
                    rows={2}
                    value={noteDraft}
                    onChange={e => setNoteDraft(e.target.value)}
                    data-testid="note-input"
                    placeholder="Spoke with CTO, they want a Q2 POC…"
                    className="w-full bg-black border border-zinc-800 px-2 py-1.5 font-mono text-[11px] text-white focus:outline-none focus:border-[#FFB000] resize-none"
                  />
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Move to</div>
                  <div className="grid grid-cols-2 gap-2">
                    {["contacted", "qualified", "won", "lost"].map(st => (
                      <button
                        key={st}
                        onClick={() => updateStatus(selectedLead.id, st)}
                        disabled={saving}
                        data-testid={`move-${st}`}
                        className="border border-zinc-700 font-mono text-[10px] uppercase tracking-widest py-2 hover:bg-zinc-900 disabled:opacity-50"
                      >{st}</button>
                    ))}
                  </div>
                </div>
                {selectedLead.notes?.length > 0 && (
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">History</div>
                    <div className="space-y-2 max-h-40 overflow-auto">
                      {selectedLead.notes.map((n, idx) => (
                        <div key={idx} className="border-l-2 border-zinc-800 pl-2 py-0.5">
                          <div className="font-mono text-[9px] text-zinc-500">{new Date(n.at).toLocaleString()} · {n.by} → <span className="text-[#FFB000]">{n.status}</span></div>
                          {n.note && <div className="font-mono text-[10px] text-zinc-300 mt-0.5">{n.note}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Panel>
          ) : (
            <Panel title="// TRIAGE">
              <div className="p-8 text-center font-mono text-[11px] text-zinc-600">
                Select a lead to view details and update status.
              </div>
            </Panel>
          )}

          {/* hot prospects */}
          <Panel title="// HOT UPSELL PROSPECTS" meta={<TrendingUp className="w-3.5 h-3.5 text-[#FFB000]" />}>
            <div className="divide-y divide-zinc-900 max-h-[400px] overflow-auto" data-testid="hot-prospects">
              {hot.length === 0 && <div className="p-6 text-center font-mono text-[10px] text-zinc-600">No upgrade clicks yet.</div>}
              {hot.slice(0, 10).map(h => (
                <div key={h.workspace_id} className="p-3" data-testid={`hot-${h.workspace_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-heading font-bold text-xs truncate">{h.last_company || h.last_user_name || "Unknown"}</span>
                    <span className="font-mono text-[10px] text-[#FFB000]">×{h.click_count}</span>
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate mt-0.5">{h.last_user_email}</div>
                  <div className="font-mono text-[9px] text-zinc-600 mt-1">
                    {h.current_plan?.toUpperCase()} · {h.last_triggered_by} · <Clock className="w-2.5 h-2.5 inline" /> {new Date(h.last_click_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-zinc-500 uppercase tracking-widest text-[9px]">{label}</span>
      <span className="text-zinc-200 text-right truncate">{value}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new:       { icon: AlertCircle,  cls: "text-[#FFB000] border-[#FFB000]/40" },
    contacted: { icon: Phone,        cls: "text-[#60A5FA] border-[#60A5FA]/40" },
    qualified: { icon: CheckCircle2, cls: "text-[#00E676] border-[#00E676]/40" },
    won:       { icon: CheckCircle2, cls: "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10" },
    lost:      { icon: XCircle,      cls: "text-[#FF3B30] border-[#FF3B30]/40" },
  };
  const s = map[status] || map.new;
  const I = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${s.cls}`}>
      <I className="w-3 h-3" /> {status}
    </span>
  );
}
